import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Autocomplete,
  Chip,
  Alert,
  Collapse
} from '@mui/material';
import React, { useContext, useState } from 'react';
import { Auth } from 'aws-amplify';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  getApplicationIndication,
  updateApplicationIndication,
  updateApplicationQcStatus
} from '../../api/FDALetters';
import Actions from '../../store/actions';
import Store from '../../store';

const ALLOWED_THERAPEUTIC_AREAS = [
  'Cardiology',
  'Nephrology',
  'Diabetes',
  'Lipid Disorders and Obesity',
  'General Endocrinology',
  'Nonmalignant Hematology',
  'Dermatology and Dentistry',
  'Gastroenterology',
  'Hepatology',
  'Nutrition',
  'Pulmonology',
  'Allergy',
  'Critical Care',
  'Rheumatology',
  'Transplant Medicine',
  'Pediatrics',
  'Urology',
  'Reproductive Medicine',
  'Maternal Health',
  'Rare Diseases',
  'Medical Genetics',
  'Obstetrics',
  'Gynecology',
  'Anti-infective',
  'Virology',
  'Ophthalmology',
  'Neurology',
  'Psychiatric',
  'Anesthetics',
  'Addiction',
  'Oncology',
  'Hematology',
  'Toxicology',
  'Imaging',
  'Autoimmune',
  'Genetic Disorder'
];

interface Application {
  id: string;
  application_number: string;
  application_type?: string;
  brand_name?: string;
}

interface TherapeuticAreaEditProps {
  letterId: string;
  applications: Application[];
  taQcStatus: string;
  closeEdit: () => void;
}

const TherapeuticAreaEdit: React.FC<TherapeuticAreaEditProps> = ({
  letterId,
  applications,
  taQcStatus,
  closeEdit
}) => {
  const { dispatch } = useContext<any>(Store);

  const [expandedApps, setExpandedApps] = useState<Set<string>>(new Set());
  const [appIndicationData, setAppIndicationData] = useState<Record<string, any>>({});
  const [loadingApps, setLoadingApps] = useState<Set<string>>(new Set());
  const [savingApps, setSavingApps] = useState<Set<string>>(new Set());
  const [savingQcStatus, setSavingQcStatus] = useState(false);
  const [taStatus, setTaStatus] = useState<string>(taQcStatus || 'to_qc');
  const [selectedAppForView, setSelectedAppForView] = useState<string>('');



  const fetchApplicationData = async (appId: string) => {
    setLoadingApps(prev => new Set(prev).add(appId));
    try {
      const response = await getApplicationIndication(letterId, appId);
      
      if (response?.data?.status === 200 && response?.data?.body) {
        const data = response.data.body;
        const app = applications.find(a => a.id === appId);
        
        // Process therapeutic areas - ensure they're in the allowed list
        let therapeuticAreas: string[] = [];
        if (Array.isArray(data.latest_indication_therapeutic_areas)) {
          therapeuticAreas = data.latest_indication_therapeutic_areas;
        } else if (data.latest_indication_therapeutic_areas) {
          therapeuticAreas = [data.latest_indication_therapeutic_areas];
        }
        
        // Filter to only include allowed therapeutic areas
        const validTherapeuticAreas = therapeuticAreas.filter((ta: string) => 
          ALLOWED_THERAPEUTIC_AREAS.includes(ta)
        );
        
        // Find filtered out therapeutic areas
        const filteredOutAreas = therapeuticAreas.filter((ta: string) => 
          !ALLOWED_THERAPEUTIC_AREAS.includes(ta)
        );
        
        if (filteredOutAreas.length > 0) {
          await dispatch({
            type: Actions.SET_ALERT,
            value: { 
              status: true, 
              message: `The following therapeutic areas from the API are not in the allowed list and were filtered out: ${filteredOutAreas.join(', ')}`,
              color: 'warning'
            }
          });
        }
        
        setAppIndicationData(prev => ({
          ...prev,
          [appId]: {
            applicationNumber: data.application_number || app?.application_number || '',
            latestIndication: data.latest_indication || '',
            therapeuticAreas: validTherapeuticAreas,
            indicationHash: data.latest_indication_hash || '',
            latestSubmission: data.latest_submission || {}
          }
        }));
      } else {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: 'Failed to load indication data' }
        });
      }
    } catch (err) {
      console.error(err);
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: 'Failed to load indication data' }
      });
    } finally {
      setLoadingApps(prev => {
        const newSet = new Set(prev);
        newSet.delete(appId);
        return newSet;
      });
    }
  };

  const toggleExpand = (appId: string) => {
    if (expandedApps.has(appId)) {
      // Collapse the currently expanded application
      setExpandedApps(new Set());
      setSelectedAppForView('');
    } else {
      // Expand the new application and collapse all others
      setExpandedApps(new Set([appId]));
      setSelectedAppForView(appId);
      if (!appIndicationData[appId]) {
        fetchApplicationData(appId);
      }
    }
  };

  const handleSaveTherapeuticAreas = async (appId: string) => {
    const data = appIndicationData[appId];
    if (!data || !data.indicationHash) {
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: 'Indication hash is missing. Cannot update.' }
      });
      return;
    }

    setSavingApps(prev => new Set(prev).add(appId));
    try {
      // Filter out empty strings from the array
      const filteredTherapeuticAreas = data.therapeuticAreas
        .map((ta: string) => ta.trim())
        .filter((ta: string) => ta.length > 0);
      
      const payload = {
        indication_hash: data.indicationHash,
        therapeutic_area: filteredTherapeuticAreas
      };

      const response = await updateApplicationIndication(letterId, appId, payload);

      if (response?.data?.status === 200) {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: 'Therapeutic areas updated successfully', color: 'success' }
        });
      } else {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: 'Failed to update therapeutic areas' }
        });
      }
    } catch (err) {
      console.error(err);
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: 'Failed to update therapeutic areas' }
      });
    } finally {
      setSavingApps(prev => {
        const newSet = new Set(prev);
        newSet.delete(appId);
        return newSet;
      });
    }
  };

  const handleSaveQcStatus = async () => {
    try {
      setSavingQcStatus(true);
      const user = await Auth.currentUserInfo();
      
      const qcStatusResponse = await updateApplicationQcStatus(letterId, {
        ta_qc_status: taStatus,
        curated_by: user.attributes.email
      });

      if (qcStatusResponse?.data?.status === 200) {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: 'TA QC Status updated successfully', color: 'success' }
        });
      } else {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: 'Failed to update TA QC Status' }
        });
      }
    } catch (err) {
      console.error(err);
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: 'Failed to update TA QC Status' }
      });
    } finally {
      setSavingQcStatus(false);
    }
  };

  const currentViewData = selectedAppForView ? appIndicationData[selectedAppForView] : null;

  return (
    <Dialog
      open={true}
      onClose={closeEdit}
      maxWidth="xl"
      fullWidth
      sx={{ backdropFilter: 'blur(5px)' }}>
      <Box p={2} position="relative">
        <IconButton
          aria-label="close"
          onClick={closeEdit}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            zIndex: 100
          }}>
          <CloseIcon />
        </IconButton>
        <Grid container>
          <Grid item xs={6} height="80vh" overflow="auto">
            <Box height="calc(100% - 180px)" overflow="auto" p={1} py={2}>
              <Stack spacing={2}>
                {applications.map((app) => {
                  const isExpanded = expandedApps.has(app.id);
                  const data = appIndicationData[app.id];
                  const isLoading = loadingApps.has(app.id);
                  const isSaving = savingApps.has(app.id);

                  return (
                    <Box key={app.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <IconButton size="small" onClick={() => toggleExpand(app.id)}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Typography variant="body1" fontWeight={600}>
                          {app.application_type} {app.application_number} {app.brand_name ? `- ${app.brand_name}` : ''}
                        </Typography>
                      </Stack>
                      
                      <Collapse in={isExpanded}>
                        {isLoading ? (
                          <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                          </Box>
                        ) : data ? (
                          <Stack spacing={2} mt={2}>
                            <Autocomplete
                              multiple
                              options={ALLOWED_THERAPEUTIC_AREAS}
                              value={data.therapeuticAreas || []}
                              onChange={(event, newValue) => {
                                setAppIndicationData(prev => ({
                                  ...prev,
                                  [app.id]: { ...prev[app.id], therapeuticAreas: newValue }
                                }));
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Latest Indication Therapeutic Areas"
                                  variant="outlined"
                                  size="small"
                                />
                              )}
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                  const { key, ...tagProps } = getTagProps({ index });
                                  return (
                                    <Chip
                                      key={key}
                                      label={option}
                                      {...tagProps}
                                    />
                                  );
                                })
                              }
                            />
                            <Button
                              onClick={() => handleSaveTherapeuticAreas(app.id)}
                              variant="contained"
                              disabled={isSaving}
                              startIcon={isSaving ? <CircularProgress size={20} /> : null}>
                              {isSaving ? 'Saving...' : 'Save Therapeutic Areas'}
                            </Button>
                          </Stack>
                        ) : null}
                      </Collapse>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
            <Stack spacing={2} pt={2} px={1}>
              <Alert severity="warning">
                Please review all {applications.length} application{applications.length !== 1 ? 's' : ''} before saving
              </Alert>
              <TextField
                variant="outlined"
                id="taStatus"
                label="TA QC Status"
                select
                value={taStatus}
                onChange={e => setTaStatus(e.target.value)}
                fullWidth
                size="small">
                <MenuItem value="qced">Qced</MenuItem>
                <MenuItem value="to_qc">To QC</MenuItem>
                <MenuItem value="escalate">Escalate</MenuItem>
                <MenuItem value="missing details">Missing Details</MenuItem>
                <MenuItem value="not applicable">Not Applicable</MenuItem>
              </TextField>
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button onClick={closeEdit} disabled={savingQcStatus}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveQcStatus}
                  variant="contained"
                  disabled={savingQcStatus}
                  startIcon={savingQcStatus ? <CircularProgress size={20} /> : null}>
                  {savingQcStatus ? 'Saving...' : 'Save QC Status'}
                </Button>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={6} height="80vh" overflow="auto">
            <Box height="90%" px={3}>
              {currentViewData ? (
                <>
                  <Stack spacing={2} mb={2}>
                    <Typography variant="h6" fontWeight={700}>
                      Application Number: {currentViewData.applicationNumber}
                    </Typography>
                    <Stack direction="row" spacing={4}>
                      <Typography variant="body2">
                        <strong>Submission Approval Date:</strong> {currentViewData.latestSubmission.approval_date || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Submission:</strong> {currentViewData.latestSubmission.submission_type || 'N/A'} - {currentViewData.latestSubmission.submission_num || 'N/A'}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Box
                    flex={1}
                    sx={{
                      height: 'calc(100% - 80px)',
                      overflow: 'auto',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'center'
                    }}>
                    {currentViewData.latestIndication ? (
                      <Typography
                        variant="body1"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word',
                          width: '100%'
                        }}>
                        <Typography fontSize={24} fontWeight="bold">Indication:</Typography>
                        {currentViewData.latestIndication}
                      </Typography>
                    ) : (
                      <Typography fontSize={24} fontWeight="bold" sx={{ textAlign: 'center' }}>
                        No indication text available
                      </Typography>
                    )}
                  </Box>
                </>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography variant="body1" color="text.secondary">
                    Expand an application to view its indication details
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default React.memo(TherapeuticAreaEdit);

